import { DayPickerPopover } from 'components/molecules/DayPickerPopover';
import { ANALYTICS_EVENT } from '@kouto/types';
import { validateAccessCode } from 'actions/access-code';
import {
  integrateSessionParticipants,
  setTierParticipants,
} from 'actions/participants';
import { useDispatch } from 'AppProvider';
import { PriceTierWithParticipants } from 'components/Booking/PriceTier/types';
import type { ITax } from 'types/experience.types';
import {
  BottomDrawer,
  BottomDrawerProps,
  BottomSheetDatePicker,
} from 'components/molecules/BottomDrawer';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import {
  Flex,
  ResponsiveFlex,
  Sep,
} from 'components/common/styled/common-styled';
import { ContinuousSessionPicker } from 'components/molecules/ContinuousSessionPicker';
import NonMappedSessionSelector from 'components/Resource/NonMappedSessionSelector';
import ResourceAccessCode, {
  AccessCodeHeading,
} from 'components/Resource/ResourceAccessCode';
import ResourceGroupSummary from 'components/Resource/ResourceGroupSummary';
import { NonMappedPriceTierSelector } from 'components/Resource/ResourcePriceTierSelector';
import { PrimaryButton, SecondaryButton } from 'components/theme/Button';
import { SkeletonPulse } from 'components/theme/Skeleton/Skeleton';
import {
  ResourceHeading2,
  ResourceParagraph,
} from 'components/theme/Typography/ResourceTypography';
import { format } from 'date-fns';
import { analyticsManager } from 'features/analytics';
import { getAnalyticsDataFromCartItem } from 'features/analytics/services/analytics.helpers';
import useAvailableDates from 'features/Reserve/hooks/useAvailableDates';
import useContinuousOptions from 'hooks/useContinuousOptions';
import useGroupViewEvent from 'features/Reserve/hooks/useGroupViewEvent';
import useStayQuote from 'hooks/useStayQuote';
import {
  describeStayLength,
  isDayBasedTimeFrame,
} from 'utils/continuousSelection';
import useResourceBookingSession from 'features/Reserve/hooks/useResourceBookingSession';
import {
  formatSessionToSessionTimeOption,
  useSessionTimeOptions,
} from 'features/Reserve/hooks/useSessionTimeOptions';
import { useTierParticipants } from 'hooks/use-selected-participants';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { formatEventDateLong } from 'utils/dateTime';
import { useCart } from 'hooks/useCart';
import moment from 'moment';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { VALIDATE_ACCESS_CODE_FAILURE } from 'types/access-code';
import { CartItemState } from 'types/cart';
import { BookingMode } from 'types/payment';
import { DATE_FORMAT, TIME_FORMAT } from 'utils';
import { defaultBreakpoints, useIsMobile } from 'WindowDimensionProvider';
import useCustomQuestions from 'api/experience/useCustomQuestions/useCustomQuestions';
import hasParticipantOrCustomQuestion from 'utils/customQuestions/hasParticipantOrCustomQuestion';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import useBookableAddOns from 'api/listing/useBookableAddOns';
import useResourceGroupOptions from 'api/experience/useResourceGroupOptions';
import isEqual from 'lodash/isEqual';
import { ResourceVariantPicker } from 'components/ResourceVariantPicker';
import {
  deriveVariantMax,
  deriveVariantRows,
  normalizeVariantCounts,
  sumVariantCounts,
  toVariantSelections,
  VariantCounts,
} from 'utils/resourceVariants/resourceVariants';

interface NonMappedGroupContentProps extends BottomDrawerProps {}

export const NonMappedGroupContent = (props: NonMappedGroupContentProps) => {
  const {
    isLoadingListing,
    resourceGroup,
    date,
    sessions,
    listing,
    groupId,
    setResourceBookingSessionParams,
    time,
    duration,
    latest,
    isInitialLoading,
    availabilityRule,
    continuousSelection,
    isContinuous,
    selectedSession: continuousSession,
    timeFrame,
  } = useResourceBookingSession();
  const { t: translate } = useTranslation();
  const languageCode = useSupportedLanguage();
  const dispatch = useDispatch();
  const { locale } = useGlobalDateLocale();
  const allSessionTimes = useSessionTimeOptions(sessions, locale);
  const isMobileView = useIsMobile();
  const { onAddToCart } = useCart();
  const [dateForCalendarAvailability, setDateForCalendarAvailability] =
    useState<moment.Moment>(date ? moment(date) : moment());
  const firstResourceGroupExperience = resourceGroup?.experiences[0];

  useEffect(() => {
    if (date) {
      setDateForCalendarAvailability(moment(date));
    }
  }, [date]);

  // A day-based stay is picked on a two-month calendar, so its availability covers the next month too.
  const isDayBased = isDayBasedTimeFrame(timeFrame);
  const { availableDates, isLoadingAvailableDates } = useAvailableDates({
    from: dateForCalendarAvailability
      .clone()
      .startOf('month')
      .format(DATE_FORMAT),
    to: dateForCalendarAvailability
      .clone()
      .add(isDayBased ? 1 : 0, 'month')
      .endOf('month')
      .format(DATE_FORMAT),
    collectionId: listing?.resourceGroupCollectionId,
    resourceGroupId: groupId,
    latest,
  });

  const { data: customQuestions, isLoading: isLoadingCustomQuestions } =
    useCustomQuestions({
      brandId: listing?.brandId,
      experienceId: firstResourceGroupExperience?.id,
      includeDefault: true,
      language: languageCode,
    });

  const { hasCustomQuestions, askParticipantsInfo } = useMemo(() => {
    return hasParticipantOrCustomQuestion(customQuestions ?? []);
  }, [customQuestions]);

  const handleDateChange = useCallback(
    (date: string) => {
      setResourceBookingSessionParams({
        date,
      });
    },
    [setResourceBookingSessionParams],
  );

  const isCartFeatureEnabled = useBrandToggleFeature('shoppingCart');
  const [, setSelectedTierCustomers] = useTierParticipants();

  const [isCodeValidated, setIsAccessCodeValidated] = useState(false);
  const [accessCodeError, setAccessCodeError] = useState('');
  // Ref, not state: the validated code is read later in the same click handler
  // that triggered validation, before the next render.
  const validatedAccessCode = useRef<{
    code: string;
    cartItemMetadata?: { experienceIds?: string[] };
  } | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const handleSessionChange = useCallback(
    (time: string | undefined, duration: number) => {
      setResourceBookingSessionParams({
        time,
        duration,
      });
    },
    [setResourceBookingSessionParams],
  );

  // A stay's start date and length arrive together from the calendar, so they reach the URL together.
  const handleStayChange = useCallback(
    (nextDate: string, duration: number) => {
      setResourceBookingSessionParams({
        date: nextDate,
        duration,
        time: undefined,
      });
    },
    [setResourceBookingSessionParams],
  );

  const [participantsPerTier, setParticipantsPerTier] = useState<
    Record<string, number>
  >({});

  // A different start or length is a different set of tiers, so the counts picked against the old
  // one are dropped rather than carried onto it.
  const handleContinuousChange = useCallback(
    (nextTime: string | undefined, nextDuration: number) => {
      handleSessionChange(nextTime, nextDuration);
      setParticipantsPerTier({});
      setPickedVariants({});
    },
    [handleSessionChange],
  );

  const { durationOptions, onDurationSelect, onStartSelect, startOptions } =
    useContinuousOptions({
      date,
      durations: resourceGroup?.durations ?? [],
      onChange: handleContinuousChange,
      rule: availabilityRule,
      selection: continuousSelection,
      spans: sessions,
      timeFrame,
    });

  const onParticipantsChange = (tierId: string, increment: number) => {
    if (!participantsPerTier[tierId]) {
      participantsPerTier[tierId] = 0;
    }
    participantsPerTier[tierId] += increment;
    setParticipantsPerTier({ ...participantsPerTier });
  };

  // A continuously booked group has no session list to pick from: the hook builds the session from
  // the guest's start and length, and it is offered here in the shape the rest of the page expects.
  const selectedSession = isContinuous
    ? continuousSession &&
      formatSessionToSessionTimeOption(continuousSession, locale)
    : allSessionTimes.find((sess) => sess.value === `${time}-${duration}`);

  const hasBookableAddOns = useBookableAddOns({
    listingId: listing?.id,
    experienceId: selectedSession?.original.experienceId,
    startDateTime: selectedSession?.original.startDateTime,
    sessionDuration: selectedSession?.original.duration,
    hasAddOns: !!listing?.addOns?.length,
  });

  // The resources open for the picked time are the ones a guest's choices can be served from.
  const openExperienceIds = useMemo(() => {
    const spans = isContinuous
      ? continuousSelection?.spans ?? []
      : sessions.filter(
          (session) =>
            selectedSession?.original.startDateTime === session.startDateTime &&
            selectedSession.original.duration === session.duration,
        );
    return Array.from(new Set(spans.map((span) => span.experienceId)));
  }, [continuousSelection, isContinuous, sessions, selectedSession]);

  const { data: resourceGroupOptions } = useResourceGroupOptions({
    experienceIds: openExperienceIds,
    resourceGroupId: groupId,
  });

  const resourceOption = resourceGroupOptions?.options[0];
  const variantRows = useMemo(
    () => (resourceOption ? deriveVariantRows(resourceOption) : []),
    [resourceOption],
  );
  const hasVariants = !!resourceOption && variantRows.length > 0;
  const tierNames = useMemo(
    () => selectedSession?.original.priceTiers?.map((tier) => tier.name) ?? [],
    [selectedSession],
  );

  // With variants to pick, the counts per variant are the quantity: a tier has no stepper of its own.
  const [pickedVariants, setPickedVariants] = useState<VariantCounts>({});
  const variantCounts = useMemo(
    () => normalizeVariantCounts(pickedVariants, tierNames, variantRows),
    [pickedVariants, tierNames, variantRows],
  );
  // Counts the free units no longer cover are dropped for good, not just hidden.
  useEffect(() => {
    if (!isEqual(variantCounts, pickedVariants)) {
      setPickedVariants(variantCounts);
    }
  }, [pickedVariants, variantCounts]);

  const resourceSelections = useMemo(
    () =>
      resourceOption && hasVariants
        ? toVariantSelections({
            counts: variantCounts,
            option: resourceOption,
            rows: variantRows,
            tierNames,
          })
        : [],
    [hasVariants, resourceOption, tierNames, variantCounts, variantRows],
  );

  const renderTierVariants = (tier: { name: string }) =>
    hasVariants && (
      <ResourceVariantPicker
        counts={variantCounts[tier.name] ?? {}}
        maxFor={(row) =>
          deriveVariantMax({
            counts: variantCounts,
            row,
            tierName: tier.name,
          })
        }
        onChange={(rowKey, count) =>
          setPickedVariants({
            ...variantCounts,
            [tier.name]: { ...variantCounts[tier.name], [rowKey]: count },
          })
        }
        rows={variantRows}
        tierName={tier.name}
      />
    );

  const priceTierParticipants = useMemo<
    (PriceTierWithParticipants & { selectedNumber: number })[]
  >(() => {
    return (
      selectedSession?.original.priceTiers?.map((t) => ({
        ...t,
        selectedNumber: hasVariants
          ? sumVariantCounts(variantCounts[t.name])
          : participantsPerTier[t.id] ?? 0,
        participants: hasVariants
          ? sumVariantCounts(variantCounts[t.name])
          : participantsPerTier[t.id] ?? 0,
      })) || []
    );
  }, [hasVariants, participantsPerTier, selectedSession, variantCounts]);

  // The picked stay priced by the server, so each tier reads as what it costs for the whole stay and
  // the summary totals what is picked at the rates each date carries.
  const { quote: stayQuote } = useStayQuote({
    enabled: isContinuous,
    experienceId: continuousSelection?.spans[0]?.experienceId,
    selection: continuousSelection,
    tiers: priceTierParticipants,
  });
  const stayPricing = useMemo(() => {
    if (!stayQuote || !continuousSelection || !timeFrame) {
      return null;
    }
    return {
      label: translate('forLength', {
        length: describeStayLength(
          continuousSelection,
          timeFrame,
          translate,
          locale,
        ),
      }),
      priceByTierName: stayQuote.stayPriceByTierName,
    };
  }, [continuousSelection, stayQuote, timeFrame, translate]);

  // A flexible or multi-day group's page opens on a start and length, so its product view reports
  // what each tier costs for that stay, the figure the guest sees, once the quote has it.
  useGroupViewEvent({
    listing,
    requiresQuotedPrice: isContinuous,
    resourceGroup,
    session: selectedSession?.original,
    priceByTierName: stayPricing?.priceByTierName,
  });

  const maxParticipants = useMemo(() => {
    // One resource per free span the selection fits inside.
    if (isContinuous) {
      return continuousSelection?.spans.length ?? 0;
    }
    if (selectedSession?.original) {
      return sessions.filter(
        (session) =>
          selectedSession.original.startDateTime === session.startDateTime &&
          selectedSession.original.duration === session.duration,
      ).length;
    }

    return 0;
  }, [continuousSelection, isContinuous, sessions, selectedSession]);

  const selectedParticipants = useMemo(() => {
    return priceTierParticipants.reduce((sum, tier) => {
      return sum + tier.participants;
    }, 0);
  }, [priceTierParticipants]);

  if (isLoadingListing) {
    return <div>{translate('loading')}...</div>;
  }

  if (!resourceGroup || !listing) {
    return <h1>{translate('noData')}</h1>;
  }

  const hasAddOns = listing?.addOns?.length > 0;

  // Beside a map this content has one narrow column, so the summary follows the form instead of
  // standing beside it.
  const isMapped = listing.isMapped ?? false;

  const isAccessCodeValidated = listing.isExclusive ? isCodeValidated : true;

  const hasANextStep = hasAddOns || askParticipantsInfo || hasCustomQuestions;

  const syncToCart = async (
    route?: string,
    state: CartItemState = CartItemState.READY,
    overrideSearch = '',
  ) => {
    const session = selectedSession?.original;
    if (!session) {
      return false;
    }
    const resource = resourceGroup.experiences[0];
    const noOfGuests = priceTierParticipants.reduce(
      (sum, data) => sum + data.participants,
      0,
    );

    if (noOfGuests === 0) {
      return false;
    }

    const participants = priceTierParticipants.flatMap((p) => {
      if (!p.participants) {
        return [];
      }
      return [
        {
          ...p,
          selectedNumber: p.participants,
        },
      ];
    });

    setSelectedTierCustomers({
      selectedParticipants: participants,
      currentExperience: resource.id,
      groupTitle: resourceGroup.title,
    });

    dispatch(
      integrateSessionParticipants({
        session,
      }),
    );

    dispatch(
      setTierParticipants({
        participants,
        currentExperience: resource.id,
        groupTitle: resourceGroup.title ?? undefined,
      }),
    );

    const cartData = {
      additionalCustomQuestionResponses: {},
      bookingMode: BookingMode.SHARED,
      experienceId: resource?.id,
      groupId,
      groupTitle: resourceGroup.title ?? undefined,
      listingId: listing.id,
      notes: '',
      participants: priceTierParticipants
        .flatMap((p) => {
          if (!p.participants) {
            return [];
          }
          return new Array(p.participants).fill(p);
        })
        .map((p, index) => ({
          resourceSelections: resourceSelections[index]?.length
            ? resourceSelections[index]
            : undefined,
          fullName: askParticipantsInfo ? '' : 'Resource Purchaser',
          firstName: askParticipantsInfo ? '' : 'Resource',
          lastName: askParticipantsInfo ? '' : 'Purchaser',
          terms: false,
          price: p.price,
          priceTierName: p.name,
        })),
      resourceGroupCollectionId:
        listing?.resourceGroupCollectionId ?? undefined,
      sessionDateTime: `${date}T${
        session.startDateTime
          ? moment(session.startDateTime).format(TIME_FORMAT)
          : undefined
      }`,
      sessionDuration: session.duration
        ? moment.duration(session.duration, 'minutes').toISOString()
        : '',
      timezone: listing?.location?.timezone,
      state,
      ...(validatedAccessCode.current?.cartItemMetadata?.experienceIds?.includes(
        resource.id,
      ) && { accessCode: validatedAccessCode.current.code }),
    };

    if (state === CartItemState.READY) {
      analyticsManager.sendEvent(
        ANALYTICS_EVENT.CLICK_ADD_TO_CART_BUTTON,
        getAnalyticsDataFromCartItem({
          cartData,
          listing,
          resourceGroupTitle: resourceGroup.title ?? undefined,
          experienceId: resource?.id,
          locationName: listing?.location?.name,
          priceByTierName: stayPricing?.priceByTierName,
        }),
      );
      analyticsManager.sendEvent(
        ANALYTICS_EVENT.CLICK_PROCEED_TO_CHECKOUT_BUTTON,
        getAnalyticsDataFromCartItem({
          cartData,
          listing,
          resourceGroupTitle: resourceGroup.title ?? undefined,
          experienceId: resource?.id,
          locationName: listing?.location?.name,
          priceByTierName: stayPricing?.priceByTierName,
        }),
      );
    }
    const result = await onAddToCart(cartData, route, overrideSearch);
    return result;
  };

  const withAccessCodeValidation = async (
    code: string,
    onSuccess: CallableFunction,
  ) => {
    if (!listing.isExclusive || isAccessCodeValidated) {
      onSuccess();
      return;
    }

    if (!code) {
      setAccessCodeError('Access code is required');
      return;
    }
    const response = await dispatch(
      validateAccessCode({
        code,
        brandId: listing?.brandId,
        experienceId: resourceGroup.experiences[0].id,
        isCartFeatureEnabled,
      }),
    );
    if (response.type === VALIDATE_ACCESS_CODE_FAILURE) {
      setIsAccessCodeValidated(false);
      setAccessCodeError('Invalid code');
      return;
    }
    validatedAccessCode.current = response.payload?.accessCode ?? null;
    onSuccess();
  };

  const onContinue = async () => {
    if (hasAddOns && (await hasBookableAddOns())) {
      await syncToCart('/addons/{cartItemId}', CartItemState.PENDING);
      return;
    }

    if (!askParticipantsInfo && !hasCustomQuestions) {
      await syncToCart('/checkout');
      return;
    }

    const params = new URLSearchParams();
    params.set('skipParticipantsName', askParticipantsInfo ? 'false' : 'true');
    if (date) {
      params.set('date', date);
    }
    if (latest) {
      params.set('latest', 'true');
    }
    const resource = resourceGroup.experiences[0];
    await syncToCart(
      `/e/${resource.id}/${
        isCartFeatureEnabled ? 'participants' : 'booking'
      }/{cartItemId}`,
      CartItemState.PENDING,
      params.toString(),
    );
  };

  const addToCart = async () => {
    if (await syncToCart()) {
      setResourceBookingSessionParams({
        groupId: undefined,
      });
    }
  };

  const onCheckout = () => {
    syncToCart('/checkout');
  };

  const onSelectDate = (date: Date) => {
    if (isLoadingAvailableDates) return;
    handleDateChange(moment(date).format(DATE_FORMAT));
    setShowDatePicker(false);
  };

  const handleContinuousRangeChange = (
    nextDate: string,
    nextDuration: number,
  ) => {
    handleStayChange(nextDate, nextDuration);
    setParticipantsPerTier({});
    setPickedVariants({});
  };

  const handleCalendarMonthChange = (month: Date) => {
    setDateForCalendarAvailability(moment(month));
  };

  const handleFixedSessionChange = ({
    original,
  }: {
    original: { duration: number; startDateTime: string };
  }) => {
    const [, nextTime] = original.startDateTime.split('T');
    handleSessionChange(nextTime, original.duration);
    setParticipantsPerTier({});
    setPickedVariants({});
  };

  // A continuously booked group picks a start and length (or a stay); a scheduled one picks a session.
  const sessionPicker =
    isContinuous && availabilityRule && timeFrame && date ? (
      <ContinuousSessionPicker
        availableDates={availableDates}
        date={date}
        durationOptions={durationOptions}
        isLoadingAvailableDates={isLoadingAvailableDates}
        onDurationSelect={onDurationSelect}
        onMonthChange={handleCalendarMonthChange}
        onRangeChange={handleContinuousRangeChange}
        onStartSelect={onStartSelect}
        rule={availabilityRule}
        selection={continuousSelection}
        startOptions={startOptions}
        timeFrame={timeFrame}
      />
    ) : (
      <NonMappedSessionSelector
        sessions={allSessionTimes}
        onChange={handleFixedSessionChange}
        selectedSession={selectedSession}
      />
    );

  const dateSelector =
    isContinuous && isDayBased ? null : (
      <DateSelectorWrapper>
        <DayPickerPopover
          asChild
          isLoading={isLoadingAvailableDates}
          open={showDatePicker}
          onOpenChange={setShowDatePicker}
          mode="single"
          required
          defaultMonth={moment(date ?? undefined).toDate()}
          selected={moment(date ?? undefined).toDate()}
          onPrevClick={(date) => setDateForCalendarAvailability(moment(date))}
          onNextClick={(date) => setDateForCalendarAvailability(moment(date))}
          className="reday-picker"
          onSelect={onSelectDate}
          footer={null}
          sideOffset={10}
          side="bottom"
          align="start"
          trigger={
            <StyledDayParagraph
              className="current-date"
              fontSize="16px"
              fontWeight="400"
              lineHeight="20px"
            >
              {date && formatEventDateLong(date, locale)}
            </StyledDayParagraph>
          }
          modifiers={{
            disabled: (date) =>
              !availableDates.includes(format(date, 'yyyy-MM-dd')) ||
              isLoadingAvailableDates,
          }}
        />
      </DateSelectorWrapper>
    );

  // A phone has no date button on the page behind the drawer, so the day is picked here, above the times.
  const mobileDateField =
    isMobileView && !isMapped && date && !(isContinuous && isDayBased) ? (
      <MobileDateField className="mobile-date-field">
        <MobileDateLabel className="mobile-date-field__label">
          {translate('date')}
        </MobileDateLabel>
        <BottomSheetDatePicker
          availableDates={availableDates}
          isCalendarLoading={isLoadingAvailableDates}
          onChange={onSelectDate}
          onMonthChange={setDateForCalendarAvailability}
          onOpenChange={setShowDatePicker}
          open={showDatePicker}
          showIcon={false}
          value={date}
        />
      </MobileDateField>
    ) : null;

  if (isMobileView && !isAccessCodeValidated) {
    return (
      <BottomDrawer {...props} heading={<AccessCodeHeading />}>
        <ResourceAccessCode
          errorMessage={accessCodeError}
          withHeading={false}
          isExclusive
          onContinue={(code) =>
            withAccessCodeValidation(code, () => {
              setIsAccessCodeValidated(true);
            })
          }
          customCallToAction={listing?.customCallToAction ?? null}
        />
      </BottomDrawer>
    );
  }

  return (
    <NonMappedGroupWrapper
      {...props}
      heading={
        isMobileView && isMapped && dateSelector ? (
          <ResponsiveFlex direction="column" alignItem="flex-start" gap={12}>
            <ResourceHeading2 className="resource-title">
              {resourceGroup.title}
            </ResourceHeading2>
            {dateSelector}
          </ResponsiveFlex>
        ) : (
          props.heading
        )
      }
    >
      <ResponsiveFlex
        direction={isMapped ? 'column' : 'row'}
        justifyContent="space-between"
        alignItem={isMapped ? 'stretch' : 'flex-start'}
        gap={isMapped ? 36 : 72}
        data-testid="resource-group-content"
      >
        <ResponsiveFlex
          flex={1}
          justifyContent="flex-start"
          alignItem={isMapped ? 'stretch' : 'flex-start'}
          direction="column"
          gap={36}
        >
          {isMobileView ? null : (
            <>
              <ResponsiveFlex
                direction="column"
                alignItem="flex-start"
                gap={12}
              >
                <ResourceHeading2 className="resource-title">
                  {resourceGroup.title}
                </ResourceHeading2>
                {dateSelector}
              </ResponsiveFlex>

              <Sep />
            </>
          )}

          {isInitialLoading ? (
            <Flex direction="row" gap={12}>
              <SkeletonPulse style={{ height: 43, width: 168 }} />
              <SkeletonPulse style={{ height: 43, width: 168 }} />
            </Flex>
          ) : (
            <>
              {mobileDateField}
              {sessionPicker}
            </>
          )}

          <Sep />
          {isInitialLoading ? (
            <Flex direction="column" gap={20} style={{ width: '100%' }}>
              <Flex
                direction="row"
                justifyContent="space-between"
                alignItem="center"
                style={{ width: '100%' }}
              >
                <SkeletonPulse style={{ height: 46, width: 120 }} />
                <SkeletonPulse style={{ height: 24, width: 80 }} />
              </Flex>
              <Flex
                direction="row"
                justifyContent="space-between"
                alignItem="center"
                style={{ width: '100%' }}
              >
                <SkeletonPulse style={{ height: 46, width: 120 }} />
                <SkeletonPulse style={{ height: 24, width: 80 }} />
              </Flex>
            </Flex>
          ) : (
            <NonMappedPriceTierSelector
              maxParticipants={maxParticipants}
              onChange={onParticipantsChange}
              priceTiers={priceTierParticipants}
              hideQuantityStepper={hasVariants}
              renderTierDetails={renderTierVariants}
              stayPricing={stayPricing}
              taxes={resourceGroup.experiences[0]?.taxes as unknown as ITax[]}
            />
          )}
          <Sep />

          <ResourceAccessCode
            errorMessage={accessCodeError}
            isExclusive={
              isMobileView ? !isAccessCodeValidated : listing?.isExclusive
            }
            renderActionButtons={(code) => (
              <>
                {hasANextStep ? (
                  <PrimaryButton
                    big
                    disabled={!selectedParticipants || isLoadingCustomQuestions}
                    onClick={() => withAccessCodeValidation(code, onContinue)}
                    className="continue-button"
                  >
                    {listing.customCallToAction || translate('continue')}
                  </PrimaryButton>
                ) : (
                  <ButtonsWrapper>
                    <SecondaryButton
                      disabled={
                        !selectedParticipants || isLoadingCustomQuestions
                      }
                      onClick={() => withAccessCodeValidation(code, addToCart)}
                      className="add-to-cart-button"
                    >
                      {translate('addToCart')}
                    </SecondaryButton>

                    <PrimaryButton
                      className="checkout-button"
                      disabled={
                        !selectedParticipants || isLoadingCustomQuestions
                      }
                      onClick={() => withAccessCodeValidation(code, onCheckout)}
                    >
                      {translate('checkout')}
                    </PrimaryButton>
                  </ButtonsWrapper>
                )}
              </>
            )}
          />
        </ResponsiveFlex>

        {!isMobileView && (
          <ResourceGroupSummary
            fullWidth={isMapped}
            priceTiers={priceTierParticipants}
            resourceGroup={resourceGroup}
            stayQuote={stayQuote}
          />
        )}
      </ResponsiveFlex>
    </NonMappedGroupWrapper>
  );
};

const NonMappedGroupWrapper = ({
  children,
  ...props
}: PropsWithChildren<BottomDrawerProps>) => {
  const isMobileView = useIsMobile();
  if (isMobileView) {
    return (
      <BottomDrawerWrapper>
        <BottomDrawer {...props}>{children}</BottomDrawer>
      </BottomDrawerWrapper>
    );
  }
  return children;
};

const MobileDateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const MobileDateLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  color: var(--way-colors-contrastColorShades-80);
`;

const DateSelectorWrapper = styled.div`
  width: fit-content;
  height: 19px;
  position: relative;
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  & button {
    width: 50%;
  }
`;

const BottomDrawerWrapper = styled.div`
  @media screen and (max-width: ${defaultBreakpoints.mobile}px) {
    display: contents;

    .drawer-header {
      height: 98px !important;
      align-items: center;
    }
  }
`;

const StyledDayParagraph = styled(ResourceParagraph)`
  white-space: nowrap;
  text-decoration: underline;
  cursor: pointer;
`;
